import React from 'react';
import { Link } from 'react-router-dom';
import homeData from '../home.json';
import productData from '../product.json';

const Home = () => {
  const { hero, sections, footer } = homeData;
  const allProducts = productData.categories.flatMap(category =>
    category.products.map(p => ({
      ...p,
      categoryName: category.name.zh,
      categoryId: category.id
    }))
  );
  const featuredProducts = allProducts.slice(0, 2);

  return (
    <main className="pt-[60px]">
      {/* Hero Banner */}
      <section className="relative min-h-[500px] flex items-center bg-surface-container-low overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            className="w-full h-full object-cover grayscale" 
            data-alt="Close-up of high-precision industrial laboratory equipment with blue liquid and sterile steel surfaces in a clean room environment" 
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuBENRkmO1_wo82Nd5Hblo9Vr6mnoM2XZf5bn6L4Df57FuyEMNG7ZJz9hlF2z2D4XeD5OirpmjetnZz-chjCoLLFdX3qeFQhZmVc5U4Grir0DxbKz-ZDWqzLZhODxGsmYD34C0LWlLzBkUhFojgM6p2W9mWcHcm2ipKj26V4mfeMFWbqN_0hTOL9jEdOow1okAq34uEveeeQ7MH7klcyhC002b3kcRM5xg5K31G6XSmpg_rGmtOVxRBHolyyRvm9Xsan9a7cH6ddVDg" 
            loading="lazy"
          />
        </div>
        <div className="container mx-auto px-6 relative z-10 py-24">
          <div className="max-w-2xl">
            <p className="text-primary font-bold text-[11px] uppercase tracking-[0.2em] mb-4">{hero.subtitle}</p>
            <h1 className="text-4xl md:text-5xl font-bold text-on-surface leading-tight mb-2">{hero.title}</h1>
            <h1 className="text-4xl md:text-5xl font-bold text-on-surface leading-tight mb-6">{hero.subTitle}</h1>
            <p className="text-on-surface-variant mb-8 max-w-xl">{hero.description}</p>
            <Link to="/contact" className="inline-block bg-primary text-on-primary px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-primary-container transition-colors duration-200">{hero.cta}</Link>
          </div>
        </div>
      </section>
      
      {/* Corporate Identity Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-primary font-bold text-[11px] uppercase tracking-widest block mb-4">{sections[0].tag}</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-on-surface">{sections[0].title}</h2>
            <h3 className="text-xl font-semibold text-primary mb-8">{sections[0].subtitle}</h3>
            <div className="space-y-6 text-on-surface-variant text-base leading-[1.8]">
              <p>{sections[0].description}</p>
              <p>{sections[0].description_zh}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Products Section */}
      <section className="py-24 bg-surface-bright">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16">
            <div>
              <span className="text-primary font-bold text-[11px] uppercase tracking-widest block mb-2">{sections[4].tag}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-on-surface">{sections[4].title}</h2>
            </div>
            <a href="/products" className="text-primary font-bold text-sm uppercase tracking-wider hover:underline mt-4 md:mt-0">{sections[4].cta}</a>
          </div>
          
          <div className="space-y-12">
            {featuredProducts.map((product, index) => (
              <Link 
                to={`/product/${product.id}`} 
                key={product.id} 
                className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 group transition-all duration-300 hover:shadow-md p-4 rounded-lg hover:-translate-y-1`}
              >
                <div className="flex-1">
                  <img 
                    src={product.image} 
                    alt={product.name.zh} 
                    className="w-full h-64 object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-on-surface mb-2 group-hover:text-primary transition-colors duration-300">{product.name.zh}</h3>
                      <h4 className="text-primary font-semibold mb-4">{product.name.en}</h4>
                    </div>
                    <span className="text-sm text-on-surface-variant">{product.series}</span>
                  </div>
                  <p className="text-on-surface-variant mb-6">{product.description.zh}</p>
                  <div className="flex flex-wrap gap-2">
                    {product.applications.map((app, i) => (
                      <span key={i} className="px-3 py-1 bg-surface-container text-sm font-medium text-on-surface-variant rounded group-hover:bg-primary/10 group-hover:text-primary transition-colors duration-300">{app}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Technical Support Section */}
      <section className="py-24 bg-surface-container-low">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-on-surface mb-4">{sections[5].title}</h2>
            <p className="text-on-surface-variant mb-8">{sections[5].description}</p>
            <div className="bg-surface-bright rounded-lg p-8 shadow-sm mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col items-center">
                  <span className="material-symbols-outlined text-primary text-3xl mb-4">phone</span>
                  <h3 className="text-lg font-semibold text-on-surface mb-2">Phone</h3>
                  <p className="text-on-surface-variant">{sections[5].contact.phone}</p>
                </div>
                <div className="flex flex-col items-center">
                  <span className="material-symbols-outlined text-primary text-3xl mb-4">email</span>
                  <h3 className="text-lg font-semibold text-on-surface mb-2">Email</h3>
                  <p className="text-on-surface-variant">{sections[5].contact.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 bg-surface-container-low">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="text-on-surface-variant text-sm">{sections[5].contact.address}</p>
            <p className="text-on-surface-variant text-sm">{footer.copyright}</p>
            <p className="text-on-surface-variant text-sm">
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="hover:underline">
                {footer.beian}
              </a>
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Home;